import React, { useContext, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import mixpanel from "mixpanel-browser";
import { AuthoritiesEnum, StatusStateEnum, TopicStatusEnum, CRUDActionEnum, AnimOrderEnum, AnimClassEnum, RoutesEnum } from "~/types/enums";
import { Text, Grid, Spinner, InputText, Icon, InputSelect, Button, Alert, Picture, Modal, Section, Link } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import topicSelectors from "~/store/topics/selectors";
import topicActionCreators from "~/store/topics/actions";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import lists from "~/lib/lists";
import TopicCard from "~/components/TopicCard";
import useAbacExt from "~/hooks/abac";
import { TopicActionEnum } from "~/store/topics/types";
import styled, { css, useTheme } from "styled-components";
import { useIntegratedSources } from "~/hooks/topics";
import Markdown from "~/components/Markdown";
import { useQueryState } from "~/hooks/common";
import CoreContext from "~/lib/context";
import TopicPickTemplateModal from "~/components/TopicPickTemplateModal";
import TopicCreateFromTemplateModal from "~/components/TopicCreateFromTemplateModal";
import TopicCreateModal from "~/components/TopicCreateModal";
import TopicUpdateModal from "~/components/TopicUpdateModal";
import TopicDeleteModal from "~/components/TopicDeleteModal";
import TopicRunRetroModal from "~/components/TopicRunRetroModal";
import TopicDuplicateModal from "~/components/TopicDuplicateModal";
import RestrictedSection from "~/components/RestrictedSection";
import { secured } from "react-abac";
import { useHistory } from "react-router-dom";
import TopicShareInSlackModal from "~/components/TopicShareInSlackModal";
import StyledIcon from "~/components/StyledIcon";
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const StyledGrid = styled(Grid)`
  position: relative;

  ${_ref => {
  let {
    $loading
  } = _ref;
  return $loading && css`
      cursor: pointer;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${_ref2 => {
    let {
      theme
    } = _ref2;
    return theme.colors.background.primary;
  }};
        opacity: 0.6;
        z-index: 1;
        transition: background-color 200ms ease-out;
      }
    `;
}}
`;
const Topics = () => {
  const theme = useTheme();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const status = useSelector(topicSelectors.getStatus);
  const action = useSelector(topicSelectors.getAction);
  const error = useSelector(topicSelectors.getError);
  const topics = useSelector(topicSelectors.getTopics);
  const sort = useSelector(topicSelectors.getSort);
  const filter = useSelector(topicSelectors.getFilter);
  const sources = useIntegratedSources();
  const {
    hasAfter
  } = useSelector(topicSelectors.getPagination);
  const history = useHistory();
  const {
    userHas
  } = useAbacExt();
  const isFiltering = !!filter.visibilities?.length || !!filter.statuses?.length || !!filter.sources?.length || filter.ownedByMe || filter.followed || filter.starred || false;
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useQueryState({
    filter
  }, (_, value) => {
    dispatch(topicActionCreators.filtered(value));
  });
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchStarted());
    }
  }, [dispatch, status]);
  const handleSort = newSort => {
    mixpanel.track(`Topics – Sort`, {
      sort: newSort
    });
    dispatch(topicActionCreators.sorted(newSort));
  };
  const handleFilter = newFilter => {
    mixpanel.track(`Topics – Filter`, {
      filter: newFilter
    });
    dispatch(topicActionCreators.filtered({
      ...filter,
      ...newFilter
    }));
  };
  const handleLoadMore = () => {
    dispatch(topicActionCreators.loadedMore());
  };
  const handleCreate = () => {
    mixpanel.track(`Topics – Create a Topic`);
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.PICK_TEMPLATE, null));
  };
  const handleBack = () => {
    mixpanel.track(`Topics – Go Back`);
    history.push(RoutesEnum.TOPIC_OVERVIEW);
  };
  const handleToggleFilterSources = id => {
    mixpanel.track(`Topics – Filter Sources`, {
      source: id
    });
    if (filter.sources?.includes(id)) {
      handleFilter({
        sources: filter.sources.filter(item => item !== id)
      });
    } else {
      handleFilter({
        sources: [...(filter.sources || []), id]
      });
    }
  };
  const handleToggleFilterVisibilities = id => {
    mixpanel.track(`Topics – Filter Visibility`, {
      visibility: id
    });
    if (filter.visibilities?.includes(id)) {
      handleFilter({
        visibilities: filter.visibilities.filter(item => item !== id)
      });
    } else {
      handleFilter({
        visibilities: [...(filter.visibilities || []), id]
      });
    }
  };
  const handleToggleFilterStatuses = id => {
    mixpanel.track(`Topics – Filter Status`, {
      status: id
    });
    if (filter.statuses?.includes(id)) {
      handleFilter({
        statuses: filter.statuses.filter(item => item !== id)
      });
    } else {
      handleFilter({
        statuses: [...(filter.statuses || []), id]
      });
    }
  };
  const handleToggleFilterOnlyOwner = () => {
    mixpanel.track(`Topics – Filter Only Owner`);
    handleFilter({
      ownedByMe: !filter.ownedByMe
    });
  };
  const handleToggleFilterStarred = () => {
    mixpanel.track(`Topics – Filter Starred`);
    handleFilter({
      starred: !filter.starred
    });
  };
  const handleToggleFilterFollowed = () => {
    mixpanel.track(`Topics – Filter Followed`);
    handleFilter({
      followed: !filter.followed
    });
  };
  const handleClearFilter = () => {
    handleFilter({
      sources: [],
      statuses: [],
      visibilities: [],
      ownedByMe: false,
      starred: false,
      followed: false
    });
  };
  if (!filter.query && !isFiltering && !topics?.length && status === StatusStateEnum.SUCCEEDED) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "sparkles"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      }
    }, t("Topics.noTopics")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "500px"
      },
      align: "center",
      text: t("Topics.noTopicsInfo")
    }), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Button, {
      elemBefore: /*#__PURE__*/React.createElement(Icon, {
        src: "plus",
        s: {
          mr: 2
        }
      }),
      onClick: handleCreate,
      s: {
        mb: 3
      }
    }, t("Topics.createFirstTopic"))), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
      open: action === TopicActionEnum.PICK_TEMPLATE
    }, /*#__PURE__*/React.createElement(TopicPickTemplateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
      open: action === TopicActionEnum.CREATE_FROM_TEMPLATE
    }, /*#__PURE__*/React.createElement(TopicCreateFromTemplateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
      open: action === CRUDActionEnum.CREATE
    }, /*#__PURE__*/React.createElement(TopicCreateModal, null)));
  }
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Link, {
    onClick: handleBack,
    weight: "semiBold",
    color: "text",
    s: {
      mt: 0,
      ml: -3
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: -90,
    s: {
      mt: -0.5
    }
  }), "Topic Overview"), /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 5
    },
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("Topics.allTopics")), /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: topics?.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      s: {
        mt: 6
      },
      center: true
    })
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    s: {
      mb: 5
    },
    template: userHas([AuthoritiesEnum.TOPICS_WRITE]) ? "1fr 180px auto" : "1fr auto",
    align: "center"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("Topics.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: sort,
    onChange: value => handleSort(value),
    color: "translucent",
    options: lists.topics.sort.names,
    fullWidth: true
  }), userHas([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Button, {
    onClick: handleCreate,
    s: {
      pl: 4,
      pr: 6,
      py: 2.5
    },
    fontSize: 3.5
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    scale: 4,
    s: {
      mr: 1,
      mt: -0.5
    }
  }), t("Topics.createTopic"))), /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 6,
    fullWidth: true,
    s: {
      mb: 4
    }
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "starFill",
      color: "warning",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.starred ? "translucentPrimary" : "translucent",
    onClick: handleToggleFilterStarred,
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, t("Topics.favorites")), /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "tick",
      color: "primary",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.followed ? "translucentPrimary" : "translucent",
    onClick: handleToggleFilterFollowed,
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, t("Topics.followed")), /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "person",
      color: "primary",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.ownedByMe ? "translucentPrimary" : "translucent",
    onClick: handleToggleFilterOnlyOwner,
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, t("Topics.ownedByMe")), Object.keys(lists.topics.visibility.names).map(key => /*#__PURE__*/React.createElement(Button, {
    key: key,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "eye",
      color: "primary",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.visibilities?.includes(key) ? "translucentPrimary" : "translucent",
    onClick: () => handleToggleFilterVisibilities(key),
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, lists.topics.visibility.names[key])), [TopicStatusEnum.COMPLETED, TopicStatusEnum.ARCHIVED].map(key => /*#__PURE__*/React.createElement(Button, {
    key: key,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "timelapse",
      color: filter.statuses?.includes(key) ? "white" : "primary",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.statuses?.includes(key) ? "translucentPrimary" : "translucent",
    onClick: () => handleToggleFilterStatuses(key),
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, lists.topics.statuses.names[key])), sources.map(source => /*#__PURE__*/React.createElement(Button, {
    key: source,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      style: {
        verticalAlign: "middle"
      },
      width: "1rem",
      height: "1rem",
      display: "inline-block",
      src: lists.common.apps.logos(theme)[source],
      border: "squared",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: filter.sources?.includes(source) ? "translucentPrimary" : "translucent",
    onClick: () => handleToggleFilterSources(source),
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, lists.topics.footprintSources.names[source])), isFiltering && /*#__PURE__*/React.createElement(Button, {
    color: "translucentDanger",
    onClick: handleClearFilter,
    s: {
      px: 2,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "cross",
      s: {
        mr: 0.5,
        mt: -0.5
      }
    })
  }, t("Topics.clearFilter")))), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, !topics?.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 8
    },
    center: true
  }) : topics?.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    $loading: status === StatusStateEnum.LOADING,
    cols: 4,
    gap: 5,
    align: "stretch",
    s: {
      mt: 5
    },
    equal: true
  }, topics.map(topic => /*#__PURE__*/React.createElement(TopicCard, {
    key: topic.id,
    topic: topic,
    shadow: true
  }))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    s: {
      mt: 8
    },
    display: "block"
  }, t("Topics.noTopicsFound")))), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.PICK_TEMPLATE
  }, /*#__PURE__*/React.createElement(TopicPickTemplateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.CREATE_FROM_TEMPLATE
  }, /*#__PURE__*/React.createElement(TopicCreateFromTemplateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(TopicCreateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(TopicUpdateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(TopicDeleteModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.DUPLICATE
  }, /*#__PURE__*/React.createElement(TopicDuplicateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.SHARE_IN_SLACK
  }, /*#__PURE__*/React.createElement(TopicShareInSlackModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.RUN_RETRO
  }, /*#__PURE__*/React.createElement(TopicRunRetroModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.TOPICS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(Topics), () => true);